import * as React from 'react'
import { formatChatDuration } from './chat_log'
import { renderMarkdown } from './markdown'

type WorkflowStageId = "reason" | "plan" | "evaluate"

interface WorkflowStageSegment {
  stage: WorkflowStageId
  content: string
  occurrence: number
  index?: number
}

const WORKFLOW_STAGE_META: Record<
  WorkflowStageId,
  { label: string; icon: string; className: string }
> = {
  reason: { label: "Reason", icon: "fa-lightbulb", className: "model-editor-chat-workflow-stage--reason" },
  plan: { label: "Plan", icon: "fa-list-check", className: "model-editor-chat-workflow-stage--plan" },
  evaluate: { label: "Evaluate", icon: "fa-clipboard-check", className: "model-editor-chat-workflow-stage--evaluate" },
}

const WORKFLOW_TAG_PATTERN = /<(reason|plan|evaluate)>([\s\S]*?)<\/(reason|plan|evaluate)>/gi

export function parseWorkflowTags(text: string): WorkflowStageSegment[] {
  const counts: Record<WorkflowStageId, number> = { reason: 0, plan: 0, evaluate: 0 }
  const segments: WorkflowStageSegment[] = []
  let match: RegExpExecArray | null

  WORKFLOW_TAG_PATTERN.lastIndex = 0
  while ((match = WORKFLOW_TAG_PATTERN.exec(text)) !== null) {
    const stage = match[1].toLowerCase() as WorkflowStageId
    const close = match[3].toLowerCase()
    if (stage !== close) continue
    counts[stage] += 1
    segments.push({
      stage,
      content: match[2].trim(),
      occurrence: counts[stage],
      index: match.index,
    })
  }

  segments.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
  return segments.map(({ stage, content, occurrence }) => ({ stage, content, occurrence }))
}

export function stripWorkflowTags(text: string): string {
  return text
    .replace(WORKFLOW_TAG_PATTERN, (_match, open: string, _body: string, close: string) =>
      open.toLowerCase() === close.toLowerCase() ? "" : _match
    )
    .trim()
}

export function hasWorkflowTags(text: string): boolean {
  return parseWorkflowTags(text).length > 0 || /<(reason|plan|evaluate)\b/i.test(text)
}

const VERDICT_LINE =
  /^\s*(?:#{1,6}\s+)?(?:\*{1,2}|_{1,2})?verdict:\s*(pass|fail)\b/i

function tidyWorkflowText(text: string): string {
  return text.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim()
}

function workflowTagOpen(line: string): WorkflowStageId | null {
  const match = line.match(/^\s*<(reason|plan|evaluate)>/i)
  return match ? (match[1].toLowerCase() as WorkflowStageId) : null
}

function workflowTagClose(line: string): WorkflowStageId | null {
  const match = line.match(/^\s*<\/(reason|plan|evaluate)>/i)
  return match ? (match[1].toLowerCase() as WorkflowStageId) : null
}

/**
 * Models often paste the evaluate write-up into the visible reply without
 * wrapping it in `<evaluate>`. Pull those `Verdict: PASS/FAIL` blocks out
 * so they can be shown in the Evaluate panel instead of the chat bubble.
 */
export function splitUntaggedEvaluate(text: string): { evaluateBlocks: string[]; remainder: string } {
  if (!text) return { evaluateBlocks: [], remainder: "" }
  const lines = text.split("\n")
  const remainderLines: string[] = []
  const evaluateBlocks: string[] = []
  let inTag: WorkflowStageId | null = null
  let i = 0

  while (i < lines.length) {
    const line = lines[i]
    if (inTag) {
      remainderLines.push(line)
      if (workflowTagClose(line) === inTag) inTag = null
      i += 1
      continue
    }
    const opened = workflowTagOpen(line)
    if (opened) {
      inTag = opened
      remainderLines.push(line)
      i += 1
      continue
    }
    if (VERDICT_LINE.test(line)) {
      const block: string[] = [line]
      i += 1
      while (
        i < lines.length &&
        lines[i].trim() !== "" &&
        !VERDICT_LINE.test(lines[i]) &&
        !workflowTagOpen(lines[i]) &&
        !workflowTagClose(lines[i])
      ) {
        block.push(lines[i])
        i += 1
      }
      if (i < lines.length && lines[i].trim() === "") i += 1
      evaluateBlocks.push(block.join("\n").trim())
      continue
    }
    remainderLines.push(line)
    i += 1
  }

  return {
    evaluateBlocks,
    remainder: tidyWorkflowText(remainderLines.join("\n")),
  }
}

export function foldUntaggedEvaluateIntoTags(text: string): string {
  const { evaluateBlocks, remainder } = splitUntaggedEvaluate(text)
  if (evaluateBlocks.length === 0) return text
  const alreadyHasEvaluate = parseWorkflowTags(text).some(segment => segment.stage === "evaluate")
  const wrapped = alreadyHasEvaluate
    ? ""
    : evaluateBlocks.map(block => `<evaluate>\n${block}\n</evaluate>`).join("\n\n")
  return tidyWorkflowText([remainder, wrapped].filter(Boolean).join("\n\n"))
}

/** Strip workflow tags from text that may appear in a user-facing assistant reply. */
export function visibleAssistantContent(content: string): string {
  const withoutTags = stripWorkflowTags(content)
    .replace(/<(reason|plan|evaluate)\b[^>]*>[\s\S]*$/i, "")
  return splitUntaggedEvaluate(withoutTags).remainder
}

/** Pull workflow-tagged text into the thinking panel; strip it from visible chat. */
export function surfaceThinkingFromTurn(
  reasoning: string,
  content: string,
  onReasoning?: (text: string) => void
): void {
  if (!onReasoning) return
  const foldedReasoning = reasoning.trim() ? foldUntaggedEvaluateIntoTags(reasoning.trim()) : ""
  const foldedContent = content.trim() ? foldUntaggedEvaluateIntoTags(content.trim()) : ""
  const parts: string[] = []
  if (foldedReasoning) parts.push(foldedReasoning)
  if (foldedContent && (hasWorkflowTags(foldedContent) || hasWorkflowTags(content))) {
    parts.push(foldedContent)
  }
  if (parts.length) onReasoning(parts.join("\n\n"))
}

export function parseEvaluateVerdict(content: string): "pass" | "fail" | null {
  if (!content) return null
  const labeled = content.match(/verdict:\s*(pass|fail)\b/i)
  if (labeled) return labeled[1].toLowerCase() as "pass" | "fail"
  const first = content.split("\n").map(line => line.trim()).find(Boolean) ?? ""
  if (/^pass\b/i.test(first)) return "pass"
  if (/^fail\b/i.test(first)) return "fail"
  return null
}

/** Latest `<evaluate>` verdict across thinking / content for this model turn. */
export function latestEvaluateVerdict(...texts: Array<string | undefined>): "pass" | "fail" | null {
  const combined = texts.filter((text): text is string => !!text && text.trim().length > 0).join("\n\n")
  if (!combined) return null
  const evaluates = parseWorkflowTags(combined).filter(segment => segment.stage === "evaluate")
  for (let i = evaluates.length - 1; i >= 0; i--) {
    const verdict = parseEvaluateVerdict(evaluates[i].content)
    if (verdict) return verdict
  }
  return parseEvaluateVerdict(combined)
}

export function hasPlanStage(text: string): boolean {
  if (!text) return false
  return parseWorkflowTags(text).some(segment => segment.stage === "plan") || /<plan\b/i.test(text)
}

function workflowStageLabel(segment: WorkflowStageSegment, totalForStage: number): string {
  const base = WORKFLOW_STAGE_META[segment.stage].label
  return totalForStage > 1 ? `${base} ${segment.occurrence}` : base
}

function workflowStagePreview(content: string): string {
  const line = content.split("\n").map(part => part.trim()).find(Boolean) ?? ""
  if (line.length <= 72) return line
  return `${line.slice(0, 69)}…`
}

function WorkflowStageDetails({
  segment,
  totalForStage,
  onActivateRef,
}: {
  segment: WorkflowStageSegment
  totalForStage: number
  onActivateRef?: (id: number) => void
}) {
  const meta = WORKFLOW_STAGE_META[segment.stage]
  const verdict = segment.stage === "evaluate" ? parseEvaluateVerdict(segment.content) : null
  const defaultOpen = segment.stage === "evaluate" && verdict === "fail"

  return (
    <details
      className={`model-editor-chat-workflow-stage ${meta.className}${
        verdict ? ` model-editor-chat-workflow-stage--${verdict}` : ""
      }`}
      open={defaultOpen || undefined}
    >
      <summary className="model-editor-chat-workflow-stage-summary">
        <i className={`fas ${meta.icon}`} aria-hidden />
        <span className="model-editor-chat-workflow-stage-title">
          {workflowStageLabel(segment, totalForStage)}
        </span>
        {verdict ? (
          <span className={`model-editor-chat-workflow-verdict model-editor-chat-workflow-verdict--${verdict}`}>
            {verdict.toUpperCase()}
          </span>
        ) : null}
        <span className="model-editor-chat-workflow-stage-preview">
          {workflowStagePreview(segment.content)}
        </span>
      </summary>
      <div className="model-editor-chat-workflow-stage-body">
        {renderMarkdown(segment.content, onActivateRef)}
      </div>
    </details>
  )
}

export function AgentWorkflowStagesPanel({
  text,
  timestamp,
  durationMs,
  onActivateRef,
}: {
  text: string
  timestamp: string
  durationMs?: number
  onActivateRef?: (id: number) => void
}) {
  const folded = foldUntaggedEvaluateIntoTags(text)
  const segments = parseWorkflowTags(folded)
  const remainder = visibleAssistantContent(folded)
  const stageTotals = segments.reduce<Record<WorkflowStageId, number>>(
    (acc, segment) => {
      acc[segment.stage] += 1
      return acc
    },
    { reason: 0, plan: 0, evaluate: 0 }
  )

  return (
    <div className="model-editor-chat-workflow">
      <details className="model-editor-chat-workflow-shell" open>
        <summary className="model-editor-chat-workflow-shell-summary">
          <i className="fas fa-diagram-project" aria-hidden />
          <span>Workflow</span>
          <span className="model-editor-chat-workflow-shell-count">
            {segments.length} stage{segments.length === 1 ? "" : "s"}
          </span>
          <span className="model-editor-chat-workflow-shell-time">
            {timestamp}
            {typeof durationMs === "number" ? (
              <span style={{ marginLeft: 6, opacity: 0.7, fontVariantNumeric: "tabular-nums" }}>
                {formatChatDuration(durationMs)}
              </span>
            ) : null}
          </span>
        </summary>
        <div className="model-editor-chat-workflow-stages">
          {segments.map((segment, index) => (
            <WorkflowStageDetails
              key={`${segment.stage}-${segment.occurrence}-${index}`}
              segment={segment}
              totalForStage={stageTotals[segment.stage]}
              onActivateRef={onActivateRef}
            />
          ))}
          {remainder ? (
            <details className="model-editor-chat-workflow-stage model-editor-chat-workflow-stage--other">
              <summary className="model-editor-chat-workflow-stage-summary">
                <i className="fas fa-brain" aria-hidden />
                <span className="model-editor-chat-workflow-stage-title">Other reasoning</span>
              </summary>
              <div className="model-editor-chat-workflow-stage-body">
                {renderMarkdown(remainder, onActivateRef)}
              </div>
            </details>
          ) : null}
        </div>
      </details>
    </div>
  )
}

